import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useFamily } from 'src/clients/hooks/useFamily';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import EmailContentModal, {
  type EditableEmailFields,
} from 'src/common/components/EmailContentModal';
import { useEmailDocument } from 'src/common/hooks/useEmailDocument';
import {
  useUpdateEmailContent,
  type EmailContentResponse,
} from 'src/meeting-note/hooks/useGenerateEmailContent';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  UnifiedDocument,
  useAllClientDocuments,
} from 'src/suitability-report/hooks/useAllClientDocuments';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from 'src/data-capture/file-management/FileUploadLayout';
import { useDeleteGeneratedDocument } from 'src/data-capture/file-management/hooks/useDeleteGeneratedDocument';
import { useDownloadDocumentFile } from 'src/data-capture/file-management/hooks/useDownloadDocumentFile';
import EmptyView from 'src/data-capture/file-management/components/EmptyView';
import GenerateDocumentsControl from 'src/data-capture/file-management/components/GenerateDocumentsControl';
import GeneratedDocumentInlineActions from 'src/data-capture/file-management/components/GeneratedDocumentInlineActions';
import RenameDocumentControl from 'src/data-capture/file-management/components/RenameDocumentControl';
import SelectionHeaderActions from 'src/data-capture/file-management/components/SelectionHeaderActions';
import {
  buildEditAction,
  buildGeneratedDocActions,
  emailTypeFor,
} from 'src/data-capture/file-management/components/generatedDocActions';
import { useBulkDocumentSelection } from 'src/data-capture/file-management/hooks/useBulkDocumentSelection';
import BulkDeleteConfirmModal from 'src/data-capture/file-management/components/BulkDeleteConfirmModal';
import {
  GeneratedDocumentType,
  GENERATED_DOCUMENT_TYPE_LABELS,
  GENERATED_DOCUMENT_TYPE_LABELS_PLURAL,
  matchesGeneratedDocumentSearch,
} from 'src/data-capture/file-management/components/generatedDocumentTypes';
import { type GeneratedDocument } from 'src/data-capture/file-management/hooks/useGeneratedDocuments';
import FilesTable from 'src/data-capture/file-management/files-table/FilesTable';
import {
  generatedDocumentCreatedColumn,
  generatedDocumentStatusColumn,
  generatedDocumentTypeColumn,
  toGeneratedDocumentRow,
  type GeneratedDocumentRow,
} from 'src/data-capture/file-management/files-table/generatedDocumentColumns';
import { resolveDocumentEditOwner } from 'src/document-editor/documentOwner';

type DocumentToDelete = {
  id: string;
  clientId?: string;
  familyId?: string;
  displayName: string;
  documentType: GeneratedDocumentType;
};

type FamilyGeneratedDocumentsProps = {
  documentTypeFilter?: GeneratedDocumentType;
};

const GENERATED_TYPES: GeneratedDocumentType[] = [
  'suitability-report',
  'meeting-agenda',
  'data-export',
  'meeting-note',
];

export default function FamilyGeneratedDocuments({
  documentTypeFilter,
}: FamilyGeneratedDocumentsProps) {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const flags = useFlags();

  const { family } = useFamily(familyId);
  const familyName = family?.name;

  const contextClientId = searchParams.get('member') ?? family?.mainFamilyMember.clientId ?? null;

  const {
    documents: unifiedDocuments,
    isLoading,
    error,
    refetch,
  } = useAllClientDocuments({
    type: 'family',
    familyId,
    poll: true,
  });
  const {
    deleteDocument,
    deleteFamilyDocument,
    deleteErrorFor,
    resetErrors,
    isDeleting: isDeletingDocument,
  } = useDeleteGeneratedDocument();
  const { downloadFile, downloadFileViaAnchor } = useDownloadDocumentFile({ familyId });

  const { handleEmailClick, handleDeleteEmail, isDeleting: isDeletingEmail } = useEmailDocument();
  const { updateEmailContent, isSaving } = useUpdateEmailContent();

  const [searchValue, setSearchValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentToDelete | null>(null);
  const [emailContent, setEmailContent] = useState<EmailContentResponse | null>(null);
  const [emailDocumentLabel, setEmailDocumentLabel] = useState<string | undefined>();
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleEmailPress = async (document: GeneratedDocument) => {
    const emailType = emailTypeFor(document);
    if (!emailType) return;

    const result = await handleEmailClick(document.id, emailType, {
      clientId: document.clientId,
      familyId: document.familyId,
    });
    if (result) {
      setEmailContent(result);
      setEmailDocumentLabel(document.displayName);
      setEmailModalOpen(true);
    }
  };

  const handleDeleteEmailContent = () => {
    if (!emailContent) return;
    handleDeleteEmail(emailContent.id);
    setEmailContent(null);
    setEmailModalOpen(false);
  };

  const handleSaveEmailContent = async (fields: EditableEmailFields) => {
    if (!emailContent) return;
    try {
      await updateEmailContent({ emailContentId: emailContent.id, ...fields });
      setEmailContent(prev => (prev ? { ...prev, ...fields } : prev));
    } catch (e) {
      notifyAndLogError(
        'Failed to save email draft',
        'An error occurred while saving the email draft. Please try again.',
        e,
        { emailContentId: emailContent.id },
      );
    }
  };

  const openFlow = (documentType: GeneratedDocumentType) => {
    if (!contextClientId) return;
    navigate(`/client/${contextClientId}/${documentType}`, {
      state: { fromPath: location.pathname, familyId },
    });
  };

  const handleDeletePress = (document: DocumentToDelete) => {
    resetErrors();
    setDocumentToDelete(document);
  };

  const deleteError = documentToDelete ? deleteErrorFor(documentToDelete.documentType) : null;

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    const { clientId, familyId: docFamilyId, id, documentType } = documentToDelete;
    try {
      if (docFamilyId) {
        await deleteFamilyDocument(docFamilyId, id, documentType);
      } else if (clientId) {
        await deleteDocument(clientId, id, documentType);
      }
    } catch (e) {
      notifyAndLogError(
        'Failed to delete document',
        'An error occurred while trying to delete the document. Please try again later.',
        e,
        { familyId, clientId, documentId: id, documentType },
      );
    }
    setDocumentToDelete(null);
    await refetch();
  };

  const title = documentTypeFilter
    ? GENERATED_DOCUMENT_TYPE_LABELS_PLURAL[documentTypeFilter]
    : 'Generated documents';

  const documents: GeneratedDocument[] = unifiedDocuments
    .filter((doc): doc is UnifiedDocument & { documentType: GeneratedDocumentType } =>
      GENERATED_TYPES.includes(doc.documentType as GeneratedDocumentType),
    )
    .map(toGeneratedDocument)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const typedDocuments = documentTypeFilter
    ? documents.filter(doc => doc.documentType === documentTypeFilter)
    : documents;

  const filteredDocuments = searchValue.trim()
    ? typedDocuments.filter(doc => matchesGeneratedDocumentSearch(doc, searchValue))
    : typedDocuments;

  const additionalColumns = documentTypeFilter
    ? [generatedDocumentCreatedColumn, generatedDocumentStatusColumn]
    : [generatedDocumentCreatedColumn, generatedDocumentTypeColumn];

  const renderGenerateControl = (buttonText: string) => (
    <GenerateDocumentsControl
      documentTypeFilter={documentTypeFilter}
      openFlow={openFlow}
      buttonText={buttonText}
    />
  );

  const rows = filteredDocuments.map(toGeneratedDocumentRow);

  const {
    selection,
    selectedRecords: selectedRows,
    bulkDeleteNames,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  } = useBulkDocumentSelection({
    records: rows,
    entityName: 'document',
    nameOf: row => row.document.displayName,
    deleteRecord: row => {
      if (row.document.familyId) {
        return deleteFamilyDocument(
          row.document.familyId,
          row.document.id,
          row.document.documentType,
        );
      }
      if (row.document.clientId) {
        return deleteDocument(row.document.clientId, row.document.id, row.document.documentType);
      }
      return Promise.resolve();
    },
    downloadRecord: row => {
      if (row.document.fileId == null) return;
      return downloadFileViaAnchor(row.document.fileId, row.document.displayName);
    },
    refetch,
    logContext: { familyId },
  });
  const hasDownloadableSelection = selectedRows.some(row => row.document.fileId != null);

  const renderNameSuffix = (row: GeneratedDocumentRow) =>
    row.document.renameFileId !== undefined ? (
      <RenameDocumentControl
        fileId={row.document.renameFileId}
        currentName={row.document.displayName}
        variant="plain"
      />
    ) : null;

  const renderInlineActions = (row: GeneratedDocumentRow) => (
    <GeneratedDocumentInlineActions
      doc={row.document}
      onDownload={downloadFile}
      editAction={buildEditAction(
        row.document,
        resolveDocumentEditOwner(row.document.clientId, row.document.familyId),
        navigate,
        flags,
        location.pathname,
      )}
      showGeneratingIndicator={!documentTypeFilter}
    />
  );

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (typedDocuments.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title={title} familyId={familyId} familyName={familyName} variant="compact">
        <EmptyView
          title="Your AI-generated documents will appear here"
          subtitle="Generate suitability reports, meeting agendas, and data exports with Siesta AI. All your AI-created documents will be organised in one convenient location."
          customCta={renderGenerateControl('Generate new')}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title={selection.isSelecting ? 'Select the files' : title}
      familyId={familyId}
      familyName={familyName}
      variant="compact"
      collapsibleSearch={!selection.isSelecting}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={filteredDocuments.length}
      searchPlaceholder="Search documents"
      actions={
        selection.isSelecting ? (
          <SelectionHeaderActions
            selectedCount={selectedRows.length}
            onDeleteSelected={requestBulkDelete}
            onDownloadSelected={downloadSelected}
            onCancel={selection.exit}
            isDownloading={isBulkDownloading}
            downloadDisabled={!hasDownloadableSelection}
          />
        ) : (
          renderGenerateControl('Generate')
        )
      }
    >
      <FilesTable<GeneratedDocumentRow>
        entitiesWithFile={rows}
        isLoading={isLoading}
        additionalColumns={additionalColumns}
        nameSubtitle={() => undefined}
        nameSuffix={renderNameSuffix}
        inlineActions={renderInlineActions}
        actions={row =>
          buildGeneratedDocActions({
            doc: row.document,
            onEmail: () => handleEmailPress(row.document),
            onSelect: () => selection.enter(row.id),
            onDelete: () =>
              handleDeletePress({
                id: row.document.id,
                clientId: row.document.clientId,
                familyId: row.document.familyId,
                displayName: row.document.displayName,
                documentType: row.document.documentType,
              }),
          })
        }
        variant="compact"
        nameColumnTitle="File name"
        selection={
          selection.isSelecting
            ? {
                selectedIds: selection.selectedIds,
                onToggle: selection.toggle,
              }
            : undefined
        }
      />

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete document"
        message={`Are you sure you want to delete "${documentToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeletingDocument}
      />

      <BulkDeleteConfirmModal
        fileNames={bulkDeleteNames}
        entityName="document"
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />

      <EmailContentModal
        opened={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        content={emailContent}
        onDelete={handleDeleteEmailContent}
        onSave={handleSaveEmailContent}
        isDeleting={isDeletingEmail}
        isSaving={isSaving}
        clientName={familyName}
        documentLabel={emailDocumentLabel}
      />
    </FileUploadLayout>
  );
}

function toGeneratedDocument(
  doc: UnifiedDocument & { documentType: GeneratedDocumentType },
): GeneratedDocument {
  return {
    id: doc.id,
    clientId: doc.clientId,
    familyId: doc.familyId,
    documentType: doc.documentType,
    createdAt: doc.createdAt,
    fileId: doc.fileId,
    file: doc.file,
    backgroundProcess: doc.backgroundProcess,
    documentHistoryId:
      doc.documentType === 'suitability-report' ? undefined : doc.documentHistoryId,
    generatedFromAgenda: doc.documentType === 'meeting-note' ? doc.generatedFromAgenda : undefined,
    fileName: doc.file?.fileName,
    // Renamable only when the shown name is the file's own name; when it falls back
    // to a config or type label (see documentDisplayName) there is nothing to rename.
    renameFileId: doc.file?.fileName ? doc.fileId : undefined,
    displayName: documentDisplayName(doc),
  };
}

function documentDisplayName(
  doc: UnifiedDocument & { documentType: GeneratedDocumentType },
): string {
  if (doc.file?.fileName) return doc.file.fileName;
  if (doc.documentType === 'data-export' && doc.configDisplayName) return doc.configDisplayName;
  return GENERATED_DOCUMENT_TYPE_LABELS[doc.documentType];
}
